/**
 * White-label (PLS) theming helpers shared by every SPA.
 *
 * `applyBranding(branding)` writes the branding as CSS custom properties on
 * :root and lazily injects the Google Fonts <link> for the chosen family, so
 * the auth screens (Login / Register / Forgot / Reset) render with the
 * organization's colors, fonts and sizes instead of the generic MeasureUp look.
 *
 * The server is the source of truth (GET /api/branding); this only paints it.
 */

export const DEFAULT_BRANDING = {
  organization_id: null,
  name: 'MeasureUp',
  white_label: false,
  logo_url: 'https://www.measureup.com/media/logo/default/logo_measureup_1_.png',
  colors: {
    primary: '#0080c9',
    brand_bg: '#0d1f3c',
    text: '#0f172a',
  },
  font_family: 'Nunito Sans',
  font_sizes: {
    base: '14px',
    heading: '26px',
  },
  terms_url: 'https://www.measureup.com/terms-of-use',
  privacy_url: 'https://www.measureup.com/privacy-policy',
};

/** Clamp a channel to 0..255. */
function clamp(v) {
  return Math.max(0, Math.min(255, v));
}

/** Parse a #rgb / #rrggbb hex color into [r,g,b], or null if invalid. */
function parseHex(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return null;
  const num = parseInt(h, 16);
  if (Number.isNaN(num)) return null;
  return [(num >> 16) & 0xff, (num >> 8) & 0xff, num & 0xff];
}

/** Format an [r,g,b] triple as #rrggbb. */
function toHex(rgb) {
  return '#' + rgb.map((c) => clamp(Math.round(c)).toString(16).padStart(2, '0')).join('');
}

/** Darken a #rgb / #rrggbb hex color by `amount` (0..1). Returns #rrggbb. */
export function darken(hex, amount = 0.15) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return toHex(rgb.map((c) => c * (1 - amount)));
}

/** Lighten a #rgb / #rrggbb hex color towards white by `amount` (0..1). */
export function tint(hex, amount = 0.85) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return toHex(rgb.map((c) => c + (255 - c) * amount));
}

/** Merge a (possibly partial) branding payload onto the defaults. */
export function normalizeBranding(branding) {
  const b = branding || {};
  return {
    ...DEFAULT_BRANDING,
    ...b,
    colors: { ...DEFAULT_BRANDING.colors, ...(b.colors || {}) },
    font_sizes: { ...DEFAULT_BRANDING.font_sizes, ...(b.font_sizes || {}) },
  };
}

/**
 * Lazily inject the Google Fonts stylesheet for `family`. No-ops for the
 * default family (already loaded by index.html) and when already injected.
 */
export function loadGoogleFont(family) {
  if (typeof document === 'undefined' || !family) return;
  const id = 'pls-font-' + family.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  if (document.getElementById(id)) return;

  const spec = encodeURIComponent(family).replace(/%20/g, '+');
  const href =
    `https://fonts.googleapis.com/css2?family=${spec}:wght@400;500;600;700;800;900&display=swap`;

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Apply a branding payload as CSS custom properties on :root and load its font.
 */
export function applyBranding(branding) {
  if (typeof document === 'undefined') return;
  const b = normalizeBranding(branding);
  const root = document.documentElement;

  const primary = b.colors.primary || DEFAULT_BRANDING.colors.primary;
  const fontStack =
    `'${b.font_family}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

  const brandBg = b.colors.brand_bg || DEFAULT_BRANDING.colors.brand_bg;

  root.style.setProperty('--pls-primary', primary);
  root.style.setProperty('--pls-primary-dark', darken(primary, 0.16));
  root.style.setProperty('--pls-brand-bg', brandBg);
  root.style.setProperty('--pls-text', b.colors.text || DEFAULT_BRANDING.colors.text);
  root.style.setProperty('--pls-font', fontStack);
  root.style.setProperty('--pls-font-size-base', b.font_sizes.base || DEFAULT_BRANDING.font_sizes.base);
  root.style.setProperty('--pls-font-size-heading', b.font_sizes.heading || DEFAULT_BRANDING.font_sizes.heading);

  // The font is safe to apply globally (a11y themes don't override typography).
  root.style.setProperty('--font-sans', fontStack);

  loadGoogleFont(b.font_family);
  applyAppTheme(primary, brandBg, fontStack);
}

/**
 * Inject the brand colors as the authenticated-app tokens (`--accent`,
 * `--brand-navy`, `--primary`...) used by AppShell and the learn-app dashboard.
 *
 * These are scoped to `html[data-theme="default"]` on purpose: the user's
 * accessibility themes (dark / high-contrast / athena) set the same tokens via
 * their own `[data-theme=...]` rules later in the cascade, so those win and the
 * brand color only paints the default theme (a11y-wins).
 */
function applyAppTheme(primary, brandBg, fontStack) {
  if (typeof document === 'undefined') return;

  const accent = primary;
  const accentDark = darken(primary, 0.16);
  const accentLight = tint(primary, 0.82);
  const accentSoft = tint(primary, 0.9);
  const navy = brandBg;
  const navy2 = darken(brandBg, 0.18);
  const navyHover = darken(brandBg, 0.3);

  const css =
    `html[data-theme="default"]{` +
    `--accent:${accent};` +
    `--accent-dark:${accentDark};` +
    `--accent-light:${accentLight};` +
    `--accent-soft:${accentSoft};` +
    `--primary:${accent};` +
    `--primary-2:${accentDark};` +
    `--primary-soft:${accentSoft};` +
    `--brand-navy:${navy};` +
    `--brand-navy-2:${navy2};` +
    `--brand-navy-hover:${navyHover};` +
    `--font-sans:${fontStack};` +
    `}`;

  let el = document.getElementById('pls-app-theme');
  if (!el) {
    el = document.createElement('style');
    el.id = 'pls-app-theme';
    document.head.appendChild(el);
  }
  el.textContent = css;
}
